import styled, { keyframes, css } from "styled-components";

// ─── Animations ─────────────────────────────────────────────────────────────

const bounceIn = keyframes`
  0%   { transform: scale(1); }
  20%  { transform: scale(1.12); }
  40%  { transform: scale(0.96); }
  60%  { transform: scale(1.06); }
  80%  { transform: scale(0.99); }
  100% { transform: scale(1); }
`;

const glowFlash = keyframes`
  0%, 100% { text-shadow: none; }
  50% {
    text-shadow:
      0 0 8px rgba(var(--color-brand-400-rgb), 0.55),
      0 0 16px rgba(var(--color-brand-600-rgb), 0.25);
  }
`;

// ─── In-flow container — sits directly below the CollectionFilter ──────────
//  Pages render this themselves below the filter chips. Padding is kept
//  tight so the bar tucks right under the filter row instead of floating
//  with whitespace around it.

export const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
  z-index: 5;
  margin-top: 8px;
  margin-bottom: 0;
  /* In feed view the page has offPadding (Content padding-top: 0) and all
     the row above the tabs (CollectionFilter, CreateInputBar, etc.) is
     hidden — leaving the tabs sitting under the fixed Header. Pin them
     just below the Header so they stay visible while the feed list
     scrolls underneath. FeedList already reserves margin-top: 150px so
     there's no overlap. */
  html.feed-scroll-mode & {
    position: fixed;
    top: 84px;
    left: 50%;
    right: auto;
    width: 100%;
    max-width: 1080px;
    transform: translateX(-50%);
    padding: 0 16px;
    margin-bottom: 0;
    z-index: 1001;
    pointer-events: none;
    margin-top: 0;
  }

  @media (max-width: 768px) {
    padding: 0;

    html.feed-scroll-mode & {
      top: 12px;
      padding: 0 12px;
    }
  }
`;

// ─── Tabs row — frosted-pill matching CollectionFilter CategoryChip ─────────
//  Same gradient + border + box-shadow recipe as the inactive Trending /
//  New / Top Rated chips so the toolbar reads as a continuation of the
//  filter row. $scrolled prop is kept for back-compat but no longer flips
//  the look.

export const TabsBar = styled.nav`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 52px;
  padding: 0 8px;
  pointer-events: auto;
  border-radius: 999px;

  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.88) 0%,
    rgba(var(--glass-pale-rgb), 0.66) 55%,
    rgba(var(--glass-pale-rgb), 0.5) 100%
  );
  box-shadow:
    0 3px 7px rgba(var(--color-brand-500-rgb), 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.92),
    inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.2);

  [data-theme="dark"] & {
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  transition:
    background 260ms var(--ease-snappy, cubic-bezier(0.4, 0, 0.2, 1)),
    border-color 260ms var(--ease-snappy, cubic-bezier(0.4, 0, 0.2, 1)),
    box-shadow 260ms var(--ease-snappy, cubic-bezier(0.4, 0, 0.2, 1));

  @media (max-width: 768px) {
    height: 48px;
    gap: 2px;
    padding: 0 6px;
  }
`;

// ─── Individual tab button ──────────────────────────────────────────────────

export const TabItem = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  width: 120px;
  height: 100%;
  padding: 0 18px;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-family-primary);
  font-size: 17px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  letter-spacing: -0.01em;
  line-height: normal;
  white-space: nowrap;
  cursor: pointer;
  z-index: 2;
  color: ${({ $active }) =>
    $active ? "transparent" : "var(--color-text-muted)"};
  transition:
    color 220ms var(--ease-snappy),
    font-weight 220ms var(--ease-snappy);

  text-decoration: ${({ $active }) => ($active ? "underline" : "none")};

  ${({ $active }) =>
    $active &&
    css`
      background: linear-gradient(
        180deg,
        var(--color-brand-400) 0%,
        var(--color-brand-600) 60%,
        var(--color-brand-700) 100%
      );
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    `}

  &:hover {
    color: ${({ $active }) =>
      $active ? "transparent" : "var(--color-text-body)"};
  }

  /* Tab icon: color-animated, gradient-filled when active */
  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    color: ${({ $active }) =>
      $active ? "var(--color-brand-600)" : "var(--color-text-muted)"};
    transition: color 220ms var(--ease-snappy);
    ${({ $active }) =>
      $active &&
      css`
        filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.25));
      `}
  }

  &:hover svg {
    color: ${({ $active }) =>
      $active ? "var(--color-brand-600)" : "var(--color-text-body)"};
  }

  @media (max-width: 768px) {
    width: 104px;
    padding: 0 14px;
    font-size: 15px;
    gap: 6px;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

export const TabText = styled.span`
  font-size: 16px;
  font-weight: 800;

  ${(props) =>
    props.$isAnimating &&
    css`
      animation:
        ${bounceIn} 0.5s cubic-bezier(0.36, 0, 0.66, -0.56),
        ${glowFlash} 0.5s ease-out;
      transform-origin: center;
    `}
`;

// ─── View mode toggle (kept, restyled to frosted glass) ─────────────────────

export const ViewModeToggle = styled.button`
  width: 44px;
  height: 44px;
  min-width: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: 0;
  border-radius: 50%;
  /* Inactive state mirrors the CollectionFilter CategoryChip recipe so
     the toggle reads as part of the same toolbar family. Active state
     keeps the brand-blue fill. */
  border: 1px solid
    ${({ $active }) =>
      $active
        ? "rgba(var(--glass-white-rgb), 0.8)"
        : "rgba(var(--glass-white-rgb), 0.65)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 100%)"
      : `radial-gradient(
           120% 100% at 20% 0%,
           rgba(var(--glass-white-rgb), 0.88) 0%,
           rgba(var(--glass-pale-rgb), 0.66) 55%,
           rgba(var(--glass-pale-rgb), 0.5) 100%
         )`};
  box-shadow: ${({ $active }) =>
    $active
      ? "0 10px 22px rgba(var(--color-brand-600-rgb), 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.85)"
      : `0 3px 7px rgba(var(--color-brand-500-rgb), 0.12),
         inset 0 1px 0 rgba(255, 255, 255, 0.92),
         inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.2)`};

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow: ${({ $active }) =>
      $active
        ? `0 4px 10px rgba(0, 0, 0, 0.5),
           inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.2)`
        : `0 3px 7px rgba(0, 0, 0, 0.4),
           inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.14)`};
  }
  cursor: pointer;
  pointer-events: auto;
  color: ${({ $active }) =>
    $active ? "var(--glass-text-on-pill)" : "var(--cf-text)"};
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    background 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy),
    color 200ms var(--ease-snappy);

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    transform: translateY(-1px) scale(1.03);
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    min-width: 40px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;
